import './createEvalueringsstatistik.scss';
import {Localizer} from "../../infrastructure/localization/localizer";
import {EmblaIcon} from "../../core/components/emblaIcon/emblaIcon";
import React, {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {filterToState} from "./evalueringsstatistikFilterModel";
import {
    evalueringsstatistikReducer,
    EvalueringsstatistikState,
    setDataset,
    setLastChangedAt,
    setShouldFetchDefaultVisning,
    setShowAllDatasetAverageAndFilters,
} from "./evalueringsstatistikSlice";
import {
    applyFilters,
    evalueringsstatistikFiltersReducer,
    EvalueringsstatistikFiltersState
} from "./evalueringsstatistikFiltersSlice";
import {
    GetSpoergsmaalstypeTranslation,
    SpoergsmaalTypeEnum
} from "../../core/sharedmodels/evaluering/spoergsmaalTypeEnum";
import {UddannelsesEvalueringCardComponent} from "./components/uddannelsesstederEvalueringCard";
import {EvalueringsstatistikPageConstants} from "./components/EvalueringsstatistikPageConstants";
import {useEffectOnUpdate} from "../../infrastructure/effect";
import {DatasetModel} from "./datasetModel";
import {toState} from "./evalueringsstatistikModel";
import {addTotalAverage, getColor} from "./evalueringsstatistikUtils";
import {toDate} from "index";
import {toReduxIsoDateString} from "../../core/helpers/reduxHelper";
import {Dropdown, DropdownOption} from "../../core/components/dropdown/dropdown";
import useEnumDropdownOptions from "../../core/components/dropdown/hooks/useEnumDropdownOptions";
import {SingleValue} from "react-select";
import NumberRangeDropdownOptions from "../../core/components/dropdown/dropdownOptions/numberRangeDropdownOptions";
import PrintButton from 'core/components/button/printButton/printButton';
import useLogbogApi from 'core/hooks/useLogbogApi';
import useScreenResolution from 'core/hooks/useScreenResolution';
import {EmblaIcons} from 'core/emblaIcons';
import questionNumberTranslator from 'core/sharedmodels/evaluering/questionNumberTranslator';
import formatNumericDropdownOptionsToCustomLabel
    from 'core/components/dropdown/formatterFunctions/formatNumericDropdownOptionsToCustomLabel';
import useUserFilterPermissions from 'core/hooks/useUserFilterPermissions';
import {UddannelsesstedModel} from 'core/sharedmodels/laegeInstitution/uddannelsesstedModel';
import useUser from 'core/hooks/useUser';
import {RootTypeEnum} from 'core/sharedmodels/laegeInstitution/rootTypeEnum';
import {LinkPlacementEnum} from "../../core/components/link/LinkPlacementEnum";
import {HelpLinks} from "../../core/components/link/helpLinks";
import {
    BuildNewEvalueringsstatistikFilter
} from "../../core/componentsPage/evalueringsstatistik/evalueringsstatistikInputModel";
import {EvalueringsstatistikFiltering} from "./evalueringsstatistikFiltering";

export const CreateEvalueringsstatistik = () => {

    const [addDatasetModalOpen, setAddDatasetModalOpen] = useState(false);
    const addDatasetModalTarget = "dataset-add-modal";

    const evalueringsstatistikSliceState = useAppSelector(evalueringsstatistikReducer) as EvalueringsstatistikState;
    const evalueringsstatistikFilterSliceState = useAppSelector(evalueringsstatistikFiltersReducer) as EvalueringsstatistikFiltersState;
    const dispatch = useAppDispatch();
    const { currentUser } = useUser();

    const [spoergsmaalTypeDropdownOptions] = useEnumDropdownOptions<SpoergsmaalTypeEnum>(SpoergsmaalTypeEnum, GetSpoergsmaalstypeTranslation);
    const [spoergsmaalNumbersToShowDropdownOptions, setSpoergsmaalNumbersToShowDropdownOptions] = useState<DropdownOption<number>[]>([]);

    const [isLoading, setIsLoading] = useState(false);

    const [datasets, setDatasets] = useState<DatasetModel[]>([]);

    const showDropdowns =  evalueringsstatistikSliceState.dataset.filter(d => d.isDefaultDatasetForUser).length > 0 || evalueringsstatistikSliceState.dataset.filter(x => !x.isDefaultDatasetForUser).length > 0;

    const shouldGetDefaultVisning = currentUser.Authenticated && evalueringsstatistikSliceState.shouldFetchDefaultVisning && evalueringsstatistikSliceState.showDefaultDatasaet && EvalueringsstatistikPageConstants.getDefaultvisning;

    const { isMobile } = useScreenResolution();
    const { evalueringstatistikApi } = useLogbogApi();

    const { permissions, isLoadingPermissions } = useUserFilterPermissions();


    useEffect(() => {
        const getSpoergsmaalNumbersToShowDropdownOptions = () => {
            //get spørgsmaal dropdown options
            let antalSpoergsmaal = evalueringsstatistikFilterSliceState.spoergsmaalsType === SpoergsmaalTypeEnum.Enkeltvis
                ? EvalueringsstatistikPageConstants.numberOfQuestionsIncludingOptionalTextInput
                : EvalueringsstatistikPageConstants.numberOfGroups;

            const options = NumberRangeDropdownOptions(antalSpoergsmaal);
            const formattetOptions = formatNumericDropdownOptionsToCustomLabel(options, questionNumberTranslator);
            setSpoergsmaalNumbersToShowDropdownOptions(formattetOptions);
        }
        getSpoergsmaalNumbersToShowDropdownOptions();
    }, [evalueringsstatistikFilterSliceState.spoergsmaalsType, spoergsmaalTypeDropdownOptions])

    useEffect(() => {
        const fetchDefaultDataset = async () => {

            if (shouldGetDefaultVisning && !isLoadingPermissions) {
                setIsLoading(true);

                const regioner = (await evalueringstatistikApi.getRegionerSelect()).filter(x => permissions.regionIds.includes(x.id)).map(x => x.text);
                const sygehuse = (await evalueringstatistikApi.getSygehuseSelect(permissions.regionIds)).filter(x => permissions.sygehusIds.includes(x.id)).map(x => x.text);
                const specialer = (await evalueringstatistikApi.getSpecialerSelect()).filter(x => permissions.specialeIds.includes(x.id)).map(x => x.text);

                const afdelinger = (await evalueringstatistikApi.getAfdelingerSelect(permissions.regionIds, permissions.sygehusIds)).filter(x => permissions.afdelingIds.includes(x.id))
                const praksiser = (await evalueringstatistikApi.getPraksisSelect(permissions.regionIds)).filter(x => permissions.praksisIds.includes(x.id));
                const uddannelsessteder = [...UddannelsesstedModel.FromSelectModel(afdelinger), ...UddannelsesstedModel.FromSelectModel(praksiser)];

                const rootTypes: RootTypeEnum[] = [];
                if (praksiser.length > 0) rootTypes.push(RootTypeEnum.Praksis);
                if (sygehuse.length > 0 || afdelinger.length > 0) rootTypes.push(RootTypeEnum.Sygehus);

                let datasetstemp: DatasetModel[] = []
                for (let i = 0; i <= EvalueringsstatistikPageConstants.antalDefaultVisninger; i++) {
                    const startDate = new Date((new Date().getUTCFullYear() - i), 0, 1);
                    const endDate = i === 0 ? new Date().getUtcWithoutTime().addMonths(1) : new Date((new Date().getUTCFullYear() - i) + 1, 0, 0);
                    const defaultEvalueringStatistik = await evalueringstatistikApi.getDefaultEvalueringStatistik(startDate, endDate);
                    const firstEvalueringStatistik = defaultEvalueringStatistik.first();
                    let dataset = new DatasetModel(
                        defaultEvalueringStatistik,
                        uddannelsessteder,
                        getColor([...datasetstemp].length, datasetstemp),
                        firstEvalueringStatistik?.maxAntalEvalueringer,
                        startDate.dateWithoutTimeFormat(true) + " - " + endDate.dateWithoutTimeFormat(true),
                        startDate.toIsoDateTimeString(),
                        endDate.toIsoDateTimeString(),
                        specialer,
                        undefined,
                        undefined,
                        [],
                        addTotalAverage(defaultEvalueringStatistik, SpoergsmaalTypeEnum.Enkeltvis),
                        addTotalAverage(defaultEvalueringStatistik, SpoergsmaalTypeEnum.Grupperet),
                        false,
                        true,
                        regioner,
                        sygehuse,
                        rootTypes.length === 1 ? rootTypes[0] : undefined,
                        true,
                        firstEvalueringStatistik?.antalForloebEvalueretTotal
                    );

                    datasetstemp.push(dataset);
                    setDatasets([...datasetstemp]);
                }
                dispatch(setShouldFetchDefaultVisning(false));
                dispatch(setLastChangedAt(toReduxIsoDateString(new Date())));
                setIsLoading(false)
            }
        }

        fetchDefaultDataset();
    }, [dispatch, evalueringstatistikApi, isLoadingPermissions, permissions, shouldGetDefaultVisning])

    useEffect(() => {
        if (evalueringsstatistikSliceState.selectedFilterId !== undefined && evalueringsstatistikSliceState.dataset.every(x => x.activeFilterId !== evalueringsstatistikSliceState.selectedFilterId)) {
            const fetchDefaultDataset = async () => {
                setIsLoading(true);
                if (evalueringsstatistikFilterSliceState.startDato && evalueringsstatistikFilterSliceState.slutDato) {
                    const requestModel = BuildNewEvalueringsstatistikFilter(evalueringsstatistikFilterSliceState, toDate(evalueringsstatistikFilterSliceState.startDato), toDate(evalueringsstatistikFilterSliceState.slutDato));
                    const filteredEvalueringStatistik = await evalueringstatistikApi.getStatistik(requestModel);
                    const firstEvalueringStatistik = filteredEvalueringStatistik.first();
                    let dataset = new DatasetModel(
                        filteredEvalueringStatistik,
                        evalueringsstatistikFilterSliceState.uddannelsessteder ?? [],
                        getColor([...evalueringsstatistikSliceState.dataset].length, evalueringsstatistikSliceState.dataset),
                        firstEvalueringStatistik?.maxAntalEvalueringer,
                        evalueringsstatistikFilterSliceState.datasetTitle,
                        toDate(evalueringsstatistikFilterSliceState.startDato)?.toIsoDateTimeString(),
                        toDate(evalueringsstatistikFilterSliceState.slutDato)?.toIsoDateTimeString(),
                        evalueringsstatistikFilterSliceState.specialer?.map(x => x.name).sort((a, b) => a.localeCompare(b)),
                        evalueringsstatistikFilterSliceState.uddannelsestyper?.map(x => x.id),
                        evalueringsstatistikFilterSliceState.uddannelsestyper?.map(x => x.name),
                        evalueringsstatistikFilterSliceState.specialer?.map(x => x.id) ?? undefined,
                        addTotalAverage(filteredEvalueringStatistik, SpoergsmaalTypeEnum.Enkeltvis),
                        addTotalAverage(filteredEvalueringStatistik, SpoergsmaalTypeEnum.Grupperet),
                        evalueringsstatistikFilterSliceState.includeInactive,
                        false,
                        evalueringsstatistikFilterSliceState.regioner?.map(x => x.name).sort((a, b) => a.localeCompare(b)),
                        evalueringsstatistikFilterSliceState.sygehuse?.map(x => x.name).sort((a, b) => a.localeCompare(b)),
                        evalueringsstatistikFilterSliceState?.laegeinstitutionstype,
                        false,
                        firstEvalueringStatistik?.antalForloebEvalueretTotal,
                        false,
                        evalueringsstatistikSliceState.selectedFilterId
                    );

                    dispatch(setDataset(toState({dataset: [...evalueringsstatistikSliceState.dataset, dataset]})));
                    dispatch(setLastChangedAt(toReduxIsoDateString(new Date())));
                }
                setIsLoading(false)
            }
            fetchDefaultDataset();
        }
    }, [dispatch, evalueringsstatistikFilterSliceState, evalueringstatistikApi])

    useEffectOnUpdate(() => {
        dispatch(setDataset(toState({dataset: datasets})));
    },[datasets])

    const spoergsmaalTypeSelected = (newValue: SingleValue<DropdownOption<SpoergsmaalTypeEnum>>) => {
        if (newValue) {
            dispatch(applyFilters(filterToState({spoergsmaalToShow: []})));
            dispatch(applyFilters(filterToState({spoergsmaalsType: newValue.value})));
        }
    }

    // On Change Handlers:
    const handleSpoergsmaalNumbersSelected = (selectedOptionKeys?: string[]) => {
        dispatch(applyFilters(filterToState({spoergsmaalToShow: selectedOptionKeys})))
    }

    return (
        <div id="create-evalueringsstatistik-container">
            <div className="card">
                <div className="card-body wrap d-flex align-items-center">
                    <h3>{currentUser.Authenticated ? Localizer.global_evalueringsstatistik() : Localizer.createEvalueringsstatistikPage_gennemsnit()}</h3>
                    <HelpLinks linkPlacement={LinkPlacementEnum.EvalueringsstatistikPage} additionalClasses={"margin-left-s"}/>
                    <div className="actions flex-align-r flex-container-row">
                        <PrintButton
                            onBeforePrint={() => dispatch(setShowAllDatasetAverageAndFilters(true))}
                            onAfterPrint={() => dispatch(setShowAllDatasetAverageAndFilters(false))}
                            show={evalueringsstatistikSliceState.dataset.length > 0}
                            additionalClassNames="margin-right-s"
                        />
                        <button id="create-dataset-btn" className="btn btn-primary"
                                onClick={() => setAddDatasetModalOpen(true)}
                                data-toggle="modal" data-target={`#${addDatasetModalTarget}`}>
                            <EmblaIcon iconName={EmblaIcons.Plus}/>
                            {Localizer.createEvalueringsstatistikPage_addDataset()}
                        </button>
                    </div>
                </div>

                { showDropdowns &&
                    <div className={"flex-container-row flex-space wrap"}>
                        <div id="graph-filter-dropdowns-container" className={`flex-container wrap margin-left-m ${isMobile ? "flex-column" : "align-items-center"}`}>
                            {!isMobile ? <h5 className="subtle margin-right-s">{Localizer.evalueringPage_visSpoergsmaal()}</h5> : "" }
                            <div className={`${isMobile ? "margin-right-m" : "margin-right-s"}`}>
                                <Dropdown
                                    placeholder={Localizer.evalueringPage_visSpoergsmaal()}
                                    options={spoergsmaalTypeDropdownOptions}
                                    isClearable={false}
                                    value={spoergsmaalTypeDropdownOptions.filter(x => x.value === evalueringsstatistikFilterSliceState.spoergsmaalsType)}
                                    onChange={spoergsmaalTypeSelected}/>
                            </div>
                            {!isMobile ? <h5 className="subtle margin-right-s">{Localizer.evalueringPage_ogVis()}</h5> : "" }
                            <div className={`margin-right-m ${isMobile ? "margin-top-m" : ""}`}>
                                <Dropdown
                                    id={"sporgsmaal-to-show"}
                                    placeholder={Localizer.evalueringPage_spoergsmaal()}
                                    closeMenuOnSelect={false}
                                    isClearable
                                    value={spoergsmaalNumbersToShowDropdownOptions.filter(x => evalueringsstatistikFilterSliceState.spoergsmaalToShow?.includes(x.value.toString()))}
                                    options={spoergsmaalNumbersToShowDropdownOptions}
                                    onChange={(newVal) => handleSpoergsmaalNumbersSelected(newVal!.map((option) => option.value.toString()))}
                                    isMulti
                                />
                            </div>
                        </div>
                    </div>

                }
                <UddannelsesEvalueringCardComponent/>
                <EvalueringsstatistikFiltering
                    setOpenModal={setAddDatasetModalOpen}
                    openModal={addDatasetModalOpen}
                    modalId={addDatasetModalTarget} isLoading={isLoading}
                    setIsLoading={setIsLoading}
                />
            </div>
        </div>
    )
}
